import { bulkUpdateFeatureAccessRequest, parseFeatureAccessError } from 'api/requests/feature-access-requests';
import { FeatureAccessFeature } from 'constants/FeatureAccessFeature';
import { useCallback, useRef, useState } from 'react';
import {
  FEATURE_ACCESS__COMPANY_NOT_FOUND,
  FEATURE_ACCESS__CONCURRENT_CHANGE,
  GENERAL__ERROR_MESSAGE,
} from 'translations/constants';
import { IBulkFeatureAccessResponse, IFeatureState } from 'types/feature-access/IFeatureAccess';
import { BulkOption, BulkOptions, buildBulkChanges, DEFAULT_BULK_OPTIONS, getFeatureAccessErrorKey } from '../utils';

export interface BulkApplied {
  response: IBulkFeatureAccessResponse;
  changes: IFeatureState[];
}

export interface UseFeatureAccessBulkCallbacks {
  onApplied: (result: BulkApplied) => void;
  onNotFound: () => void;
  onStale: () => void;
  /** A request failed while the bulk panel is closed (a blocked-company retry, or the panel was closed mid-save). */
  onError: (messageKey: string) => void;
}

export const useFeatureAccessBulk = (callbacks: UseFeatureAccessBulkCallbacks) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<BulkOptions>(DEFAULT_BULK_OPTIONS);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveErrorKey, setSaveErrorKey] = useState<string | null>(null);
  const savingRef = useRef(false);
  // Read after an await, so it must reflect the panel as it is now, not as it was when the request started.
  const isOpenRef = useRef(false);
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const close = useCallback(() => {
    isOpenRef.current = false;
    setIsOpen(false);
    setIsConfirmOpen(false);
    setOptions(DEFAULT_BULK_OPTIONS);
    setSaveErrorKey(null);
  }, []);

  const open = useCallback(() => {
    isOpenRef.current = true;
    setOptions(DEFAULT_BULK_OPTIONS);
    setSaveErrorKey(null);
    setIsOpen(true);
  }, []);

  const setOption = useCallback((feature: FeatureAccessFeature, option: BulkOption) => {
    setOptions(previous => ({ ...previous, [feature]: option }));
  }, []);

  /** Nothing set means nothing to apply, so the panel closes without a request (AC "Bulk save with every feature left unchanged"). */
  const requestSave = useCallback(() => {
    if (buildBulkChanges(options).length === 0) {
      close();
      return;
    }
    setIsConfirmOpen(true);
  }, [options, close]);

  const cancelConfirm = useCallback(() => setIsConfirmOpen(false), []);

  const send = useCallback(
    async (companyIds: number[], changes: IFeatureState[], closeOnSuccess: boolean) => {
      if (savingRef.current) return;
      savingRef.current = true;
      setIsSaving(true);
      setSaveErrorKey(null);
      try {
        const response = await bulkUpdateFeatureAccessRequest(companyIds, changes);
        if (closeOnSuccess) close();
        callbacksRef.current.onApplied({ response, changes });
      } catch (error) {
        const parsed = parseFeatureAccessError(error);
        setIsConfirmOpen(false);
        if (parsed.key === FEATURE_ACCESS__COMPANY_NOT_FOUND) {
          close();
          callbacksRef.current.onNotFound();
        } else {
          if (parsed.key === FEATURE_ACCESS__CONCURRENT_CHANGE) callbacksRef.current.onStale();
          const messageKey = getFeatureAccessErrorKey(parsed, GENERAL__ERROR_MESSAGE);
          // An error bar inside a closed panel is never seen, so it becomes a toast instead.
          if (isOpenRef.current) setSaveErrorKey(messageKey);
          else callbacksRef.current.onError(messageKey);
        }
      } finally {
        savingRef.current = false;
        setIsSaving(false);
      }
    },
    [close],
  );

  const confirm = useCallback(
    (companyIds: number[]) => send(companyIds, buildBulkChanges(options), true),
    [send, options],
  );

  /** Re-sends the same changes for the blocked companies only. The user already confirmed them. */
  const retry = useCallback(
    (companyIds: number[], changes: IFeatureState[]) => send(companyIds, changes, false),
    [send],
  );

  return {
    isOpen,
    options,
    isConfirmOpen,
    isSaving,
    saveErrorKey,
    open,
    close,
    setOption,
    requestSave,
    cancelConfirm,
    confirm,
    retry,
  };
};

export type UseFeatureAccessBulkResult = ReturnType<typeof useFeatureAccessBulk>;
